import { ApiError } from "@aptedge/lib-ui/src/utils/ApiError";
import { logger } from "@aptedge/lib-ui/src/utils/logger";
import {
  decorateHeadersWithAuth,
  makeTimedResponse,
  TimedResponse
} from "@aptedge/lib-ui/src/utils/request";
import { MULTIPLE_CHOICES, OK, UNAUTHORIZED } from "http-status-codes";
/* eslint @typescript-eslint/no-explicit-any: 0 */

interface TypedResponse<T = any> extends Response {
  json<P = T>(): Promise<P>;
}

/**
 * Parses the JSON returned by a network request
 */
function parseJSON<T>(response: TypedResponse<T>): Promise<T> {
  return response.json();
}

/**
 * Checks status of a network response, and throws an error if something went wrong
 */
function checkStatus(url: string) {
  return async function handleErrors(response: Response): Promise<Response> {
    if (response.status === UNAUTHORIZED) {
      const { pathname, search } = window.location;
      // Only redirect and dispatch redirect action once.
      if (!pathname.includes("/login") && !pathname.includes("/logout")) {
        const redirectTo = pathname + search;
        window.dispatchEvent(
          new CustomEvent("app:unauthorized", {
            detail: { redirectTo }
          })
        );
      }
    }

    if (response.status >= OK && response.status < MULTIPLE_CHOICES) {
      return response;
    }

    let message: string | undefined;

    try {
      const text = await response.text();
      if (text.trim()) {
        try {
          const errorResponse = JSON.parse(text) as { error?: string };
          message = errorResponse.error;
        } catch {
          message = text.slice(0, 200);
        }
      }
    } catch (e) {
      logger.warn("Failed to read error response body", e);
    }

    throw new ApiError(response.status, url, message);
  };
}

// Names this surface to the backend, which records it as a conversation's
// origin for the Conversations page. Set here rather than in lib-ui's shared
// decorateHeadersWithAuth, which the support apps also call - they send their
// own value ("zendesk", "salesforce", "servicenow", "self-service").
function withAppClientType(options: RequestInit = {}): RequestInit {
  const decorated = decorateHeadersWithAuth(options);
  decorated.headers = {
    ...decorated.headers,
    "X-Aptedge-App-Client-Type": "web-app"
  };
  return decorated;
}

/**
 * Requests a URL, and returns a promise
 * @param url the URL to request
 * @param options options to pass to fetch
 *
 * @returns response data
 */
async function request<T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  return fetch(url, withAppClientType(options))
    .then(checkStatus(url))
    .then<T>(parseJSON);
}

async function timedRequest<T = any>(
  url: string,
  options: RequestInit = {}
): Promise<TimedResponse<T>> {
  const startTime = Date.now();
  return request<T>(url, options).then<TimedResponse<T>>((data) =>
    makeTimedResponse(data, startTime)
  );
}
async function rawRequest(
  url: string,
  options: RequestInit = {}
): Promise<any> {
  return fetch(url, withAppClientType(options));
}
async function deleteRequest(url: string, options: RequestInit): Promise<void> {
  return fetch(url, withAppClientType(options))
    .then(checkStatus(url))
    .then(() => Promise.resolve()); // Just return an empty Promise when request is successful.
}

export { request, deleteRequest, rawRequest, timedRequest };
